import { type FormEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { applicationsApi } from "../api/applications";
import { ApiError } from "../api/client";
import { Alert, Button, Card, Field, Input, Textarea } from "../components/ui";

/**
 * Replaces dropping a markdown file into `01-Inbox/`.
 *
 * Company and role are deliberately not asked for: the worker gets them from
 * the ad as structured output, which is what generate.py's regex over the
 * first fifteen lines (:338) was trying and failing to do.
 */
export default function NewApplication() {
  const navigate = useNavigate();
  const [jobAd, setJobAd] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");

  const create = useMutation({
    mutationFn: () => applicationsApi.create(jobAd, sourceUrl, true),
    onSuccess: (application) => navigate(`/applications/${application.id}`),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  const tooShort = jobAd.trim().length < 40;
  const message = create.error instanceof ApiError ? create.error.message : null;

  return (
    <div className="space-y-6">
      <header>
        <Link to="/" className="text-sm text-gray-500 hover:underline">
          ← All applications
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-gray-900">New application</h1>
        <p className="mt-1 text-sm text-gray-600">
          Paste the whole job ad. The more of it you include, the better the tailoring.
        </p>
      </header>

      <Card>
        <form onSubmit={onSubmit} className="space-y-4">
          {message && <Alert kind="error">{message}</Alert>}

          <Field label="Job ad">
            <Textarea
              rows={18}
              value={jobAd}
              onChange={(e) => setJobAd(e.target.value)}
              placeholder="Paste the full job listing here — title, company, location and description."
              required
            />
          </Field>

          <Field label="Link to the posting" hint="Optional, for your own reference.">
            <Input
              type="url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://…"
            />
          </Field>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={tooShort || create.isPending}>
              {create.isPending ? "Starting…" : "Generate documents"}
            </Button>
            <span className="text-xs text-gray-500">
              Takes a few minutes and costs roughly $0.30–0.60 in API usage.
            </span>
          </div>
        </form>
      </Card>
    </div>
  );
}
